import { UAParser } from "my-ua-parser"

export const BrowserSupport = {
  SUPPORTED: "SUPPORTED",
  /** Opened on a computer, where capture is impossible. Hard block. */
  DESKTOP: "DESKTOP",
  /** Mobile, but in a browser we do not test against. Skippable warning. */
  UNSUPPORTED_BROWSER: "UNSUPPORTED_BROWSER",
} as const

export type BrowserSupportState = (typeof BrowserSupport)[keyof typeof BrowserSupport]

// Browser families the capture flow is tested against, matched against my-ua-parser's
// `browser.name`. Covers every platform, since the desktop check runs first and takes precedence.
const ALLOWED_BROWSER_NAMES: readonly string[] = ["Chrome", "Safari", "Mobile Safari", "Samsung Internet"]

// Seznam reports itself as Chrome on Android and Safari on iOS, so the allowlist alone would let it
// through. Its UA token is the only reliable signal — my-ua-parser does not know the browser at all.
const SEZNAM_UA_PATTERN = /sznprohlizec/i

type IsDesktopParams = {
  osName: string | undefined
  deviceType: string | undefined
  maxTouchPoints: number
}

// Phones and tablets report a device type, desktops report none. iPadOS 13+ is the exception: it
// claims to be Mac OS with no device type, so touch points are what tell it apart from a real Mac.
function isDesktop(params: IsDesktopParams): boolean {
  const { osName, deviceType, maxTouchPoints } = params

  if (deviceType !== undefined) {
    return false
  }

  return !(osName === "Mac OS" && maxTouchPoints > 1)
}

type ResolveBrowserSupportParams = {
  userAgent: string
  maxTouchPoints: number
}

/**
 * Decides whether the capture flow may run in the current browser.
 */
export function resolveBrowserSupport(params: ResolveBrowserSupportParams): BrowserSupportState {
  const { userAgent, maxTouchPoints } = params

  const parser = new UAParser(userAgent)
  const browserName = parser.getBrowser().name
  const osName = parser.getOS().name
  const deviceType = parser.getDevice().type

  // Desktop wins over the allowlist: Chrome on a computer still cannot take photos.
  if (isDesktop({ osName, deviceType, maxTouchPoints })) {
    return BrowserSupport.DESKTOP
  }

  if (SEZNAM_UA_PATTERN.test(userAgent)) {
    return BrowserSupport.UNSUPPORTED_BROWSER
  }

  if (browserName !== undefined && ALLOWED_BROWSER_NAMES.includes(browserName)) {
    return BrowserSupport.SUPPORTED
  }

  return BrowserSupport.UNSUPPORTED_BROWSER
}

/** Same decision for the browser the app is currently running in. */
export function resolveCurrentBrowserSupport(): BrowserSupportState {
  return resolveBrowserSupport({
    userAgent: navigator.userAgent,
    maxTouchPoints: navigator.maxTouchPoints,
  })
}
